// ============================================================================
// FSA DEPRECIATION CALCULATION ENGINE  (ADR-0006)
// ============================================================================
// Pure computation over the tab's editable draft. The UI computes on every
// edit and the results are persisted verbatim on Save (save-compute-display-
// read) — the backend never re-derives, and print/Excel/suggestions read the
// saved figures.
//
// Working-paper conventions (Phase 1):
//   • Book (WDV): dep = max(0, opening − Σ disposal NBV) × rate
//                       + Σ addition × rate × pro-rata.
//     Disposals are treated as removed at the start of the year for the
//     depreciation base (dep up to the disposal date is deliberately not
//     auto-charged — ADR-0006 §3; the auditor adjusts if material).
//   • Book (SLM, rev 7): tracked on gross cost + accumulated depreciation.
//       dep = max(0, opening cost − Σ disposal cost − fully-depreciated cost)
//             × rate + Σ addition × rate × pro-rata,
//     capped so NBV never falls below residual % × closing cost.
//     The method is chosen per class; a sub-class inherits its parent's.
//   • IT (s.32 block): dep = rate × max(0, opening + add≥180 − proceeds)
//                            + rate/2 × max(0, add<180 − spillover)
//     where spillover = proceeds not absorbed by the full-rate pool.
//   • Per-addition IT dep is INDICATIVE only — the block figure is
//     authoritative (proceeds absorption happens at block level).

import type {
  BookProrataConvention,
  DepAddition,
  DepCalcContext,
  DepCalcWorkspace,
  DepClassConfig,
  DepDisposal,
  DepItBlock,
  DepMethod,
  SaveDepCalcRequest,
} from 'api/types/fsa/depreciationCalc.types';
import type { ID } from 'api/types';

// ---------------------------------------------------------------------------
// Draft model — the tab's editable working state
// ---------------------------------------------------------------------------

export interface DraftAddition {
  localId: string;
  scheduleId: ID;
  scheduleClassId: ID | null;
  customClassLocalId: ID | null;
  classCode: string;
  /** Depreciation-internal sub-class key (rev 6); null = sits directly on the class. */
  subClassLocalId: ID | null;
  ledgerName: string | null;
  description: string;
  additionDate: string;      // YYYY-MM-DD
  putToUseDate: string;      // YYYY-MM-DD
  amount: number;
  itBlockEnumId: ID | null;
  itRateOverride: number | null;
  displayOrder: number;
  // computed
  daysUsed: number | null;
  isLt180Days: boolean | null;
  bookDepActual: number | null;
  bookDepRounded: number | null;
  itDepIndicativeActual: number | null;
  itDepIndicativeRounded: number | null;
}

export interface DraftDisposal {
  localId: string;
  scheduleId: ID;
  scheduleClassId: ID | null;
  customClassLocalId: ID | null;
  classCode: string;
  /** Depreciation-internal sub-class key (rev 6); null = sits directly on the class. */
  subClassLocalId: ID | null;
  ledgerName: string | null;
  description: string;
  originalCost: number;
  accumulatedDepreciation: number;
  disposalDate: string;      // YYYY-MM-DD
  disposalProceeds: number;
  itBlockEnumId: ID | null;
  displayOrder: number;
  // computed
  /** Rev 9 — book dep from the start of the year to the disposal date; null when not charged. */
  depToDisposalActual: number | null;
  depToDisposalRounded: number | null;
  /** Written-down value at the date of sale. */
  netBookValue: number | null;
  profitLossOnDisposal: number | null;
}

export interface DraftItBlock {
  itBlockEnumId: ID;
  enumKey: string | null;
  label: string | null;
  openingWdv: number;
  rate: number;
  /** User flag: block ceased with a positive balance → remainder is an IT loss. */
  isMarkedExhausted: boolean;
  // computed
  additionsGe180: number | null;
  additionsLt180: number | null;
  disposalProceeds: number | null;
  itDepActual: number | null;
  itDepRounded: number | null;
  closingWdvActual: number | null;
  closingWdvRounded: number | null;
  isBlockNegative: boolean;
  itProfitLoss: number | null;
}

export interface DepDraft {
  roundingPrecision: number;
  taxRate: number | null;
  bookProrataConvention: BookProrataConvention;
  /** Rev 8 — WDV class book rates mirror their IT block rates (NCE). */
  bookRatesFollowIt: boolean;
  /** Rev 8 — false = deferred tax not computed. */
  dtEnabled: boolean;
  /** Rev 9 — disposals are charged book dep up to the date of sale. */
  depToDisposalDate: boolean;
  dtOpeningPosition: number | null;
  dtClosingPositionActual: number | null;
  dtClosingPositionRounded: number | null;
  dtChargeForYearActual: number | null;
  dtChargeForYearRounded: number | null;
  classConfig: Record<string, DepClassConfig>;
  additions: DraftAddition[];
  disposals: DraftDisposal[];
  itBlocks: DraftItBlock[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const roundTo = (v: number, precision: number): number => {
  const f = Math.pow(10, precision);
  return Math.round((v + Number.EPSILON) * f) / f;
};

const dayMs = 86_400_000;

const toUtcDay = (iso: string): number => {
  const d = iso.slice(0, 10);
  return Date.parse(`${d}T00:00:00Z`);
};

/** Inclusive day span; NaN-safe (returns 0 for unparseable dates). */
const daysBetweenInclusive = (fromIso: string, toIso: string): number => {
  const a = toUtcDay(fromIso);
  const b = toUtcDay(toIso);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / dayMs) + 1;
};

export const newLocalId = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * The classConfig key an entry aggregates under: its sub-class local id when
 * it sits on a sub-class, else its class code.
 */
export const entryConfigKey = (e: { classCode: string; subClassLocalId: ID | null }): string =>
  e.subClassLocalId != null ? String(e.subClassLocalId) : e.classCode;

// ---------------------------------------------------------------------------
// Method helpers (rev 7)
// ---------------------------------------------------------------------------

/** Schedule II PPE residual value is up to 5% of cost; intangibles default to nil. */
export const defaultResidualPct = (scheduleCode: string | null | undefined): number =>
  scheduleCode === 'NOTE_INTANGIBLE_ASSETS' ? 0 : 5;

/** Method in force: the entry's own choice, else (sub-class) the parent's, else WDV. */
export const effectiveMethod = (cfg: DepClassConfig, classConfig: Record<string, DepClassConfig>): DepMethod =>
  cfg.method
    ?? (cfg.parent_class_code ? classConfig[cfg.parent_class_code]?.method : null)
    ?? 'WDV';

/** Residual % in force: the entry's own value, else (sub-class) the parent's, else the schedule default. */
export const effectiveResidualPct = (cfg: DepClassConfig, classConfig: Record<string, DepClassConfig>): number =>
  cfg.residual_pct
    ?? (cfg.parent_class_code ? classConfig[cfg.parent_class_code]?.residual_pct : null)
    ?? defaultResidualPct(cfg.schedule_code);

/**
 * Rate % p.a. from useful life + residual value (Schedule II basis):
 *   SLM: (100 − residual) / life
 *   WDV: (1 − (residual/100)^(1/life)) × 100 — undefined for a nil residual.
 * Rounded to 2 dp, as rates are quoted. null = cannot derive.
 */
export const rateFromLife = (method: DepMethod, life: number | null | undefined, residualPct: number): number | null => {
  if (life == null || life <= 0) return null;
  if (method === 'SLM') return roundTo((100 - residualPct) / life, 2);
  if (residualPct <= 0 || residualPct >= 100) return null;
  return roundTo((1 - Math.pow(residualPct / 100, 1 / life)) * 100, 2);
};

/**
 * Resolves the per-entry method state before any computation: effective
 * method, derived rate (rate_from_life), and for SLM entries the stored
 * residual and the opening WDV implied by the gross-cost basis. An entry
 * that becomes SLM with no basis yet starts at cost = current opening WDV,
 * accumulated 0, so the carrying amount is preserved until the auditor
 * enters the real split.
 */
const resolveMethods = (
  classConfig: Record<string, DepClassConfig>,
  itRateFor: ((code: string, cfg: DepClassConfig) => number | null) | null,
): Record<string, DepClassConfig> => {
  const out: Record<string, DepClassConfig> = {};
  for (const [code, cfg] of Object.entries(classConfig)) {
    const method = effectiveMethod(cfg, classConfig);
    const residual = effectiveResidualPct(cfg, classConfig);
    const next: DepClassConfig = { ...cfg, method_effective: method, rate_source: 'OWN' };
    // Rate precedence: from life (explicit ↺) → IT block rate (rev 8, when
    // the calc follows IT rates, WDV lines only — IT rates are WDV rates,
    // and an entry can opt out) → the entry's own typed rate.
    const derived = cfg.rate_from_life ? rateFromLife(method, cfg.useful_life_years, residual) : null;
    const itRate = derived == null && itRateFor && method === 'WDV' && cfg.rate_follows_it !== false
      ? itRateFor(code, cfg)
      : null;
    if (derived != null) {
      next.book_rate = derived;
      next.rate_source = 'LIFE';
    } else if (itRate != null) {
      next.book_rate = itRate;
      next.rate_source = 'IT';
    }
    if (method === 'SLM') {
      next.residual_pct = residual;
      if (next.opening_gross_cost == null && next.opening_accum_dep == null) {
        next.opening_gross_cost = cfg.opening_wdv ?? 0;
        next.opening_accum_dep = 0;
      }
      next.opening_wdv = (next.opening_gross_cost ?? 0) - (next.opening_accum_dep ?? 0);
    } else {
      // Drop SLM-only computed keys so a switch back to WDV leaves no stale
      // figures (the SLM inputs stay, in case the auditor switches back).
      delete next.closing_gross_cost;
      delete next.closing_accum_dep_actual;
      delete next.closing_accum_dep_rounded;
      delete next.dep_capped;
    }
    out[code] = next;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Draft construction
// ---------------------------------------------------------------------------

/**
 * Builds the editable draft from a loaded workspace. classConfig is merged
 * with the live class options so every fixed-asset class gets an entry.
 *
 * Opening WDV is NOT derived from the mapped ledgers' TB balances (rev 3 —
 * ledger mapping is optional and must never be the WDV basis): it comes
 * from the previous year's depreciation calc (roll-forward, Phase 1c) or
 * manual entry. New classes start at 0.
 */
export const buildDraft = (ws: DepCalcWorkspace): DepDraft => {
  const h = ws.header;
  const classConfig: Record<string, DepClassConfig> = {};

  for (const opt of ws.context.classOptions) {
    const existing = h.classConfig[opt.classCode];
    classConfig[opt.classCode] = existing
      ? { ...existing, schedule_code: opt.scheduleCode }
      : {
          schedule_code: opt.scheduleCode,
          book_rate: null,
          opening_wdv: 0,
        };
  }
  // Keep saved entries whose class no longer appears in the options
  // (declassified / removed custom class) — never silently drop data.
  for (const [code, cfg] of Object.entries(h.classConfig)) {
    if (!classConfig[code]) classConfig[code] = { ...cfg };
  }

  return {
    roundingPrecision: h.roundingPrecision,
    taxRate: h.taxRate,
    bookProrataConvention: h.bookProrataConvention,
    bookRatesFollowIt: h.bookRatesFollowIt ?? false,
    dtEnabled: h.dtEnabled ?? true,
    depToDisposalDate: h.depToDisposalDate ?? false,
    dtOpeningPosition: h.dtOpeningPosition,
    dtClosingPositionActual: h.dtClosingPositionActual,
    dtClosingPositionRounded: h.dtClosingPositionRounded,
    dtChargeForYearActual: h.dtChargeForYearActual,
    dtChargeForYearRounded: h.dtChargeForYearRounded,
    classConfig,
    additions: ws.additions.map(additionToDraft),
    disposals: ws.disposals.map(disposalToDraft),
    itBlocks: ws.itBlocks.map(blockToDraft),
  };
};

const additionToDraft = (a: DepAddition): DraftAddition => ({
  localId: newLocalId(),
  scheduleId: a.scheduleId,
  scheduleClassId: a.scheduleClassId,
  customClassLocalId: a.customClassLocalId,
  classCode: a.classCode ?? '',
  subClassLocalId: a.subClassLocalId,
  ledgerName: a.ledgerName,
  description: a.description,
  additionDate: a.additionDate.slice(0, 10),
  putToUseDate: a.putToUseDate.slice(0, 10),
  amount: a.amount,
  itBlockEnumId: a.itBlockEnumId,
  itRateOverride: a.itRateOverride,
  displayOrder: a.displayOrder,
  daysUsed: a.daysUsed,
  isLt180Days: a.isLt180Days,
  bookDepActual: a.bookDepActual,
  bookDepRounded: a.bookDepRounded,
  itDepIndicativeActual: a.itDepIndicativeActual,
  itDepIndicativeRounded: a.itDepIndicativeRounded,
});

const disposalToDraft = (d: DepDisposal): DraftDisposal => ({
  localId: newLocalId(),
  scheduleId: d.scheduleId,
  scheduleClassId: d.scheduleClassId,
  customClassLocalId: d.customClassLocalId,
  classCode: d.classCode ?? '',
  subClassLocalId: d.subClassLocalId,
  ledgerName: d.ledgerName,
  description: d.description,
  originalCost: d.originalCost,
  accumulatedDepreciation: d.accumulatedDepreciation,
  disposalDate: d.disposalDate.slice(0, 10),
  disposalProceeds: d.disposalProceeds,
  itBlockEnumId: d.itBlockEnumId,
  displayOrder: d.displayOrder,
  depToDisposalActual: d.depToDisposalActual ?? null,
  depToDisposalRounded: d.depToDisposalRounded ?? null,
  netBookValue: d.netBookValue,
  profitLossOnDisposal: d.profitLossOnDisposal,
});

const blockToDraft = (b: DepItBlock): DraftItBlock => ({
  itBlockEnumId: b.itBlockEnumId,
  enumKey: b.itBlockKey,
  label: b.itBlockLabel,
  openingWdv: b.openingWdv,
  rate: b.rate,
  isMarkedExhausted: b.isMarkedExhausted,
  additionsGe180: b.additionsGe180,
  additionsLt180: b.additionsLt180,
  disposalProceeds: b.disposalProceeds,
  itDepActual: b.itDepActual,
  itDepRounded: b.itDepRounded,
  closingWdvActual: b.closingWdvActual,
  closingWdvRounded: b.closingWdvRounded,
  isBlockNegative: b.isBlockNegative,
  itProfitLoss: b.itProfitLoss,
});

// ---------------------------------------------------------------------------
// Recompute — the single entry point after any edit
// ---------------------------------------------------------------------------

export const recomputeDraft = (draft: DepDraft, context: DepCalcContext): DepDraft => {
  const p = draft.roundingPrecision;

  // Ensure every live class option has a classConfig entry. Classes can
  // appear AFTER the draft was seeded (a custom class declared in the Notes
  // tab while this tab stayed mounted) — without this merge they would be
  // missing from the Book Schedule and their additions would aggregate
  // nowhere.
  const mergedClassConfig: Record<string, DepClassConfig> = { ...draft.classConfig };
  for (const opt of context.classOptions) {
    if (!mergedClassConfig[opt.classCode]) {
      mergedClassConfig[opt.classCode] = {
        schedule_code: opt.scheduleCode,
        book_rate: null,
        opening_wdv: 0,
      };
    }
  }
  // Rev 7 — method, derived rate and SLM opening WDV resolve first: the
  // per-addition book dep below reads the resolved rate.
  // Rev 8 — the IT rate of an entry's class = its (parent's) default IT
  // block, at the rate in this calc's IT schedule if the block is there
  // (the auditor may have edited it), else the block master rate.
  const itRateFor = draft.bookRatesFollowIt
    ? (code: string, cfg: DepClassConfig): number | null => {
        const opt = context.classOptions.find(o => o.classCode === (cfg.parent_class_code ?? code));
        if (!opt?.defaultItBlock) return null;
        const block = context.itBlockOptions.find(b => b.enumKey === opt.defaultItBlock);
        if (!block) return null;
        return draft.itBlocks.find(b => String(b.itBlockEnumId) === String(block.id))?.rate ?? block.rate;
      }
    : null;
  draft = { ...draft, classConfig: resolveMethods(mergedClassConfig, itRateFor) };
  const fyStart = (context.periodStart ?? '').slice(0, 10);
  const fyEnd = context.financialYearEnd.slice(0, 10);
  const daysInFy = fyStart ? Math.max(1, daysBetweenInclusive(fyStart, fyEnd)) : 365;

  const blockRateByEnumId = new Map<string, number>(
    context.itBlockOptions.map(o => [String(o.id), o.rate]),
  );

  // ---- 1 · Per-addition computed values ------------------------------------
  const additions = draft.additions.map(a => {
    const rawDays = daysBetweenInclusive(a.putToUseDate, fyEnd);
    const daysUsed = Math.min(Math.max(rawDays, 0), daysInFy);
    const isLt180 = daysUsed < 180;

    // Sub-class entries take the sub-class's own rate; fall back to the
    // parent class rate if the sub entry vanished (defensive).
    const classRate = draft.classConfig[entryConfigKey(a)]?.book_rate
      ?? draft.classConfig[a.classCode]?.book_rate
      ?? 0;
    const prorata = draft.bookProrataConvention === 'HALF_RATE_180'
      ? (isLt180 ? 0.5 : 1)
      : daysUsed / daysInFy;
    const bookDep = a.amount * (classRate / 100) * prorata;

    const itRate = a.itRateOverride
      ?? (a.itBlockEnumId != null ? blockRateByEnumId.get(String(a.itBlockEnumId)) ?? 0 : 0);
    const itDep = a.amount * (itRate / 100) * (isLt180 ? 0.5 : 1);

    return {
      ...a,
      daysUsed,
      isLt180Days: isLt180,
      bookDepActual: bookDep,
      bookDepRounded: roundTo(bookDep, p),
      itDepIndicativeActual: itDep,
      itDepIndicativeRounded: roundTo(itDep, p),
    };
  });

  // ---- 2 · Per-disposal computed values ------------------------------------
  // Rev 9 — with dep to disposal date on, the asset is charged from the
  // start of the year to its sale date at its line's rate + method (the
  // entered accumulated dep is up to the START of the year); the WDV at sale
  // and the profit/(loss) follow.
  const disposals = draft.disposals.map(d => {
    const nbvOpen = d.originalCost - d.accumulatedDepreciation;
    let inYear = 0;
    if (draft.depToDisposalDate && fyStart) {
      const cfg = draft.classConfig[entryConfigKey(d)] ?? draft.classConfig[d.classCode];
      inYear = depToDisposal({
        cost: d.originalCost,
        nbvOpen,
        disposalDate: d.disposalDate,
        method: cfg?.method_effective ?? 'WDV',
        rate: cfg?.book_rate ?? 0,
        residualPct: cfg?.method_effective === 'SLM' ? (cfg.residual_pct ?? 0) : 0,
        convention: draft.bookProrataConvention,
        fyStart,
        daysInFy,
      });
    }
    const nbv = nbvOpen - inYear;
    return {
      ...d,
      depToDisposalActual: draft.depToDisposalDate ? inYear : null,
      depToDisposalRounded: draft.depToDisposalDate ? roundTo(inYear, p) : null,
      netBookValue: roundTo(nbv, p),
      profitLossOnDisposal: roundTo(d.disposalProceeds - nbv, p),
    };
  });

  // ---- 3 · Class-level book totals -----------------------------------------
  const classConfig: Record<string, DepClassConfig> = {};
  for (const [code, cfg] of Object.entries(draft.classConfig)) {
    // Entries aggregate under their config key: the sub-class local id when
    // they sit on a sub-class, else the class code. A parent class with
    // sub-classes therefore only aggregates its DIRECT entries — the sub
    // rows carry their own; note suggestions roll them up server-side.
    const classAdditions = additions.filter(a => entryConfigKey(a) === code);
    const classDisposals = disposals.filter(d => entryConfigKey(d) === code);

    const additionsTotal = classAdditions.reduce((s, a) => s + a.amount, 0);
    const disposalsCost = classDisposals.reduce((s, d) => s + d.originalCost, 0);
    // Carrying value of the disposed assets at the START of the year — the
    // amount that leaves the depreciable base.
    const disposalsNbvOpen = classDisposals.reduce((s, d) => s + d.originalCost - d.accumulatedDepreciation, 0);
    // Dep charged on disposals within the year (rev 9) — part of the year's
    // dep AND of the accumulated dep that leaves with the assets.
    const disposalsInYearDep = classDisposals.reduce((s, d) => s + (d.depToDisposalActual ?? 0), 0);
    const disposalsAccum = classDisposals.reduce((s, d) => s + d.accumulatedDepreciation, 0) + disposalsInYearDep;
    // WDV at sale (rev 9: after any dep to the disposal date).
    const disposalsNbv = disposalsNbvOpen - disposalsInYearDep;

    const opening = cfg.opening_wdv ?? 0;
    const rate = cfg.book_rate ?? 0;
    const additionsDep = classAdditions.reduce((s, a) => s + (a.bookDepActual ?? 0), 0);

    const totals: DepClassConfig = {
      additions_total: roundTo(additionsTotal, p),
      disposals_nbv_total: roundTo(disposalsNbv, p),
      disposals_cost_total: roundTo(disposalsCost, p),
      disposals_accum_dep_total: roundTo(disposalsAccum, p),
    };

    if (cfg.method_effective === 'SLM') {
      // Gross-cost basis: disposed assets leave at cost + their accumulated
      // dep; fully depreciated assets stay in cost but earn no dep.
      const openingCost = cfg.opening_gross_cost ?? 0;
      const openingAccum = cfg.opening_accum_dep ?? 0;
      const depreciableCost = Math.max(0, openingCost - disposalsCost - (cfg.fully_depreciated_cost ?? 0));
      const rawDep = depreciableCost * (rate / 100) + additionsDep;
      const closingCost = openingCost + additionsTotal - disposalsCost;
      // Continuing assets only — disposals' in-year dep is capped per asset.
      const nbvBeforeDep = opening + additionsTotal - disposalsNbvOpen;
      const floor = closingCost * ((cfg.residual_pct ?? 0) / 100);
      const maxDep = Math.max(0, nbvBeforeDep - floor);
      const continuingDep = Math.min(rawDep, maxDep);
      const depForYear = continuingDep + disposalsInYearDep;
      const closingAccum = openingAccum - disposalsAccum + depForYear;
      const closing = nbvBeforeDep - continuingDep;
      classConfig[code] = {
        ...cfg,
        ...totals,
        dep_for_year_actual: depForYear,
        dep_for_year_rounded: roundTo(depForYear, p),
        closing_wdv_actual: closing,
        closing_wdv_rounded: roundTo(closing, p),
        closing_gross_cost: roundTo(closingCost, p),
        closing_accum_dep_actual: closingAccum,
        closing_accum_dep_rounded: roundTo(closingAccum, p),
        dep_capped: rawDep - continuingDep > 0.005,
      };
      continue;
    }

    const openingDepBase = Math.max(0, opening - disposalsNbvOpen);
    const depForYear = openingDepBase * (rate / 100) + additionsDep + disposalsInYearDep;
    const closing = opening + additionsTotal - disposalsNbv - depForYear;

    classConfig[code] = {
      ...cfg,
      ...totals,
      dep_for_year_actual: depForYear,
      dep_for_year_rounded: roundTo(depForYear, p),
      closing_wdv_actual: closing,
      closing_wdv_rounded: roundTo(closing, p),
    };
  }

  // ---- 4 · IT block schedule (authoritative s.32 mechanics) ----------------
  // Rows = existing blocks ∪ blocks referenced by any addition/disposal.
  const blockMap = new Map<string, DraftItBlock>();
  for (const b of draft.itBlocks) blockMap.set(String(b.itBlockEnumId), { ...b });
  const ensureBlock = (enumId: ID | null) => {
    if (enumId == null) return;
    const key = String(enumId);
    if (!blockMap.has(key)) {
      const opt = context.itBlockOptions.find(o => String(o.id) === key);
      blockMap.set(key, {
        itBlockEnumId: enumId,
        enumKey: opt?.enumKey ?? null,
        label: opt?.enumValue ?? null,
        openingWdv: 0,
        rate: opt?.rate ?? 0,
        isMarkedExhausted: false,
        additionsGe180: null,
        additionsLt180: null,
        disposalProceeds: null,
        itDepActual: null,
        itDepRounded: null,
        closingWdvActual: null,
        closingWdvRounded: null,
        isBlockNegative: false,
        itProfitLoss: null,
      });
    }
  };
  additions.forEach(a => ensureBlock(a.itBlockEnumId));
  disposals.forEach(d => ensureBlock(d.itBlockEnumId));

  const itBlocks = Array.from(blockMap.values()).map(b => {
    const key = String(b.itBlockEnumId);
    const ge180 = additions
      .filter(a => String(a.itBlockEnumId) === key && a.isLt180Days === false)
      .reduce((s, a) => s + a.amount, 0);
    const lt180 = additions
      .filter(a => String(a.itBlockEnumId) === key && a.isLt180Days === true)
      .reduce((s, a) => s + a.amount, 0);
    const proceeds = disposals
      .filter(d => String(d.itBlockEnumId) === key)
      .reduce((s, d) => s + d.disposalProceeds, 0);

    // Block cessation (s.50-equivalent treatment, rev 3):
    //   gross = opening + all additions − proceeds
    //   • gross < 0 → auto-exhausted: proceeds exceeded the block →
    //     IT PROFIT of −gross; no depreciation; closing 0.
    //   • gross ≥ 0 but the user marked the block exhausted (all assets
    //     disposed) → the remainder is an IT LOSS of −gross; no
    //     depreciation; closing 0.
    const gross = b.openingWdv + ge180 + lt180 - proceeds;
    const isNegative = gross < 0;
    const ceased = isNegative || b.isMarkedExhausted;

    let itDep = 0;
    let itProfitLoss: number | null = null;
    let closing = 0;
    if (ceased) {
      itProfitLoss = -gross; // positive = profit, negative = loss
    } else {
      const fullPoolRaw = b.openingWdv + ge180 - proceeds;
      const fullPool = Math.max(0, fullPoolRaw);
      // Proceeds not absorbed by the full-rate pool spill into the half-rate pool.
      const spillover = Math.max(0, -fullPoolRaw);
      const halfPool = Math.max(0, lt180 - spillover);
      itDep = fullPool * (b.rate / 100) + halfPool * (b.rate / 200);
      closing = gross - itDep;
    }

    return {
      ...b,
      additionsGe180: roundTo(ge180, p),
      additionsLt180: roundTo(lt180, p),
      disposalProceeds: roundTo(proceeds, p),
      itDepActual: itDep,
      itDepRounded: roundTo(itDep, p),
      closingWdvActual: closing,
      closingWdvRounded: roundTo(closing, p),
      isBlockNegative: isNegative,
      itProfitLoss: itProfitLoss == null ? null : roundTo(itProfitLoss, p),
    };
  });

  // ---- 5 · Deferred tax (depreciation timing difference) -------------------
  // Sign convention (rev 5, auditor presentation): POSITIVE = DTA,
  // NEGATIVE = DTL. Book WDV above the IT tax base is a taxable temporary
  // difference → DTL → negative position. The charge keeps its own
  // semantic: positive = charge to P&L (a DTL increase / DTA decrease).
  // Rev 8 — switched off: no position or charge (the DT note cross-check
  // then reports "unavailable").
  if (!draft.dtEnabled) {
    return {
      ...draft,
      additions,
      disposals,
      classConfig,
      itBlocks,
      dtClosingPositionActual: null,
      dtClosingPositionRounded: null,
      dtChargeForYearActual: null,
      dtChargeForYearRounded: null,
    };
  }
  const bookClosing = Object.values(classConfig).reduce((s, c) => s + (c.closing_wdv_actual ?? 0), 0);
  const itClosing = itBlocks.reduce((s, b) => s + (b.closingWdvActual ?? 0), 0);
  const taxRate = draft.taxRate ?? 0;
  const dtClosing = (itClosing - bookClosing) * (taxRate / 100);
  const dtCharge = (draft.dtOpeningPosition ?? 0) - dtClosing;

  return {
    ...draft,
    additions,
    disposals,
    classConfig,
    itBlocks,
    dtClosingPositionActual: dtClosing,
    dtClosingPositionRounded: roundTo(dtClosing, p),
    dtChargeForYearActual: dtCharge,
    dtChargeForYearRounded: roundTo(dtCharge, p),
  };
};

// ---------------------------------------------------------------------------
// Live preview (addition modal) — same math as recomputeDraft step 1
// ---------------------------------------------------------------------------

export interface AdditionPreview {
  daysUsed: number;
  isLt180Days: boolean;
  bookDep: number;
  itDepIndicative: number;
}

export const previewAddition = (
  args: {
    putToUseDate: string;
    amount: number;
    classRate: number;
    itRate: number;
    convention: BookProrataConvention;
  },
  context: DepCalcContext,
): AdditionPreview => {
  const fyStart = (context.periodStart ?? '').slice(0, 10);
  const fyEnd = context.financialYearEnd.slice(0, 10);
  const daysInFy = fyStart ? Math.max(1, daysBetweenInclusive(fyStart, fyEnd)) : 365;
  const rawDays = args.putToUseDate ? daysBetweenInclusive(args.putToUseDate, fyEnd) : 0;
  const daysUsed = Math.min(Math.max(rawDays, 0), daysInFy);
  const isLt180Days = daysUsed < 180;
  const prorata = args.convention === 'HALF_RATE_180'
    ? (isLt180Days ? 0.5 : 1)
    : daysUsed / daysInFy;
  return {
    daysUsed,
    isLt180Days,
    bookDep: args.amount * (args.classRate / 100) * prorata,
    itDepIndicative: args.amount * (args.itRate / 100) * (isLt180Days ? 0.5 : 1),
  };
};

// ---------------------------------------------------------------------------
// Depreciation up to the disposal date (rev 9)
// ---------------------------------------------------------------------------

/**
 * Book dep on ONE disposed asset from the start of the year to its sale
 * date. Fraction of the year: day-wise (days held ÷ days in the year) or,
 * under the 180-day convention, half rate when held < 180 days, else full.
 * WDV: carrying value at the start of the year × rate × fraction.
 * SLM: cost × rate × fraction, never below the residual value.
 * A sale on/before the first day of the year earns nothing.
 */
export const depToDisposal = (args: {
  cost: number;
  nbvOpen: number;
  disposalDate: string;
  method: DepMethod;
  rate: number;
  residualPct: number;
  convention: BookProrataConvention;
  fyStart: string;
  daysInFy: number;
}): number => {
  const { cost, nbvOpen, disposalDate, method, rate, residualPct, convention, fyStart, daysInFy } = args;
  if (!(rate > 0) || !(nbvOpen > 0) || !disposalDate) return 0;
  // Days held this year = FY start up to the day before the sale.
  const daysHeld = Math.min(Math.max(daysBetweenInclusive(fyStart, disposalDate) - 1, 0), daysInFy);
  if (daysHeld <= 0) return 0;
  const fraction = convention === 'HALF_RATE_180' ? (daysHeld < 180 ? 0.5 : 1) : daysHeld / daysInFy;
  if (method === 'SLM') {
    return Math.min(cost * (rate / 100) * fraction, Math.max(0, nbvOpen - cost * (residualPct / 100)));
  }
  return nbvOpen * (rate / 100) * fraction;
};

// ---------------------------------------------------------------------------
// Disposal carrying-value helper (rev 8)
// ---------------------------------------------------------------------------

const addYearsIso = (iso: string, years: number): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d.toISOString().slice(0, 10);
};

/**
 * Estimates the accumulated depreciation of ONE asset up to the start of
 * the current FY — the point a disposal leaves the book schedule (§3: no
 * dep in the year of disposal). Rolls the asset forward financial year by
 * financial year from its put-to-use date at the class's current rate and
 * method; the first year is pro-rated with the calc's convention. SLM stops
 * at the residual value. A helper only: the auditor can always overwrite the
 * figure (books may have used other rates in earlier years).
 * Returns null when the inputs cannot support an estimate.
 */
export const estimateAccumulatedDep = (args: {
  cost: number;
  putToUseDate: string;
  method: DepMethod;
  rate: number;
  residualPct: number;
  convention: BookProrataConvention;
  fyStart: string;
}): number | null => {
  const { cost, putToUseDate, method, rate, residualPct, convention, fyStart } = args;
  if (!(cost > 0) || !(rate > 0) || !putToUseDate || !fyStart) return null;
  if (Number.isNaN(toUtcDay(putToUseDate)) || Number.isNaN(toUtcDay(fyStart))) return null;
  if (toUtcDay(putToUseDate) >= toUtcDay(fyStart)) return 0; // put to use this year

  // Earliest FY (start date) that contains the put-to-use date.
  let years = 1;
  while (years < 100 && toUtcDay(addYearsIso(fyStart, -years)) > toUtcDay(putToUseDate)) years++;

  const floor = method === 'SLM' ? cost * (residualPct / 100) : 0;
  let carrying = cost;
  for (let k = years; k >= 1; k--) {
    const start = addYearsIso(fyStart, -k);
    const nextStart = addYearsIso(fyStart, -k + 1);
    const daysInYear = Math.round((toUtcDay(nextStart) - toUtcDay(start)) / dayMs);
    let fraction = 1;
    if (k === years) {
      const daysUsed = Math.round((toUtcDay(nextStart) - toUtcDay(putToUseDate)) / dayMs);
      fraction = convention === 'HALF_RATE_180' ? (daysUsed < 180 ? 0.5 : 1) : daysUsed / daysInYear;
    }
    const dep = method === 'SLM'
      ? Math.min(cost * (rate / 100) * fraction, Math.max(0, carrying - floor))
      : carrying * (rate / 100) * fraction;
    carrying -= dep;
  }
  return cost - carrying;
};

// ---------------------------------------------------------------------------
// Book vs Tax reconciliation (rev 8) — pure sums of the computed figures
// ---------------------------------------------------------------------------

export interface BookTaxReconRow {
  /** IT block enum id, or null for classes/disposals not mapped to a block. */
  itBlockEnumId: string | null;
  label: string;
  /** Book side — classes grouped by their (parent's) default IT block. */
  bookDep: number;
  bookClosingWdv: number;
  /** Book profit/(loss) on disposals grouped by the disposal's own IT block. */
  bookProfitLoss: number;
  /** IT side — the block's own figures. */
  itDep: number;
  itClosingWdv: number;
  /** s.50 short-term capital gain/(loss) on block cessation. */
  s50GainLoss: number;
}

export interface BookTaxRecon {
  rows: BookTaxReconRow[];
  totals: Omit<BookTaxReconRow, 'itBlockEnumId' | 'label'>;
  /** Book dep added back − IT dep allowed − book profit on sale + book loss on sale. */
  netAdjustmentToProfit: number;
}

/**
 * The book-vs-tax statement for the tax computation. Book figures sit on a
 * block through the class's default IT block (a sub-class uses its parent's);
 * disposals through the block chosen on the disposal. Classes without a
 * default block (land, goodwill, some custom classes) fall in the "not
 * mapped" row. Uses ROUNDED figures — the same ones the schedules print.
 */
export const buildBookTaxReconciliation = (draft: DepDraft, context: DepCalcContext): BookTaxRecon => {
  const blockIdByKey = new Map(context.itBlockOptions.map(o => [o.enumKey, String(o.id)]));
  const defaultBlockByClass = new Map(context.classOptions.map(o => [o.classCode, o.defaultItBlock]));
  const rowMap = new Map<string, BookTaxReconRow>();
  const NONE = '__none';
  const row = (blockId: string | null): BookTaxReconRow => {
    const key = blockId ?? NONE;
    let r = rowMap.get(key);
    if (!r) {
      const opt = blockId ? context.itBlockOptions.find(o => String(o.id) === blockId) : undefined;
      const b = blockId ? draft.itBlocks.find(x => String(x.itBlockEnumId) === blockId) : undefined;
      r = {
        itBlockEnumId: blockId,
        label: blockId ? (b?.label ?? opt?.enumValue ?? 'IT block') : 'Not mapped to an IT block',
        bookDep: 0, bookClosingWdv: 0, bookProfitLoss: 0, itDep: 0, itClosingWdv: 0, s50GainLoss: 0,
      };
      rowMap.set(key, r);
    }
    return r;
  };

  for (const b of draft.itBlocks) {
    const r = row(String(b.itBlockEnumId));
    r.itDep += b.itDepRounded ?? 0;
    r.itClosingWdv += b.closingWdvRounded ?? 0;
    r.s50GainLoss += b.itProfitLoss ?? 0;
  }
  for (const [code, cfg] of Object.entries(draft.classConfig)) {
    if ((cfg.dep_for_year_rounded ?? 0) === 0 && (cfg.closing_wdv_rounded ?? 0) === 0) continue;
    const blockKey = defaultBlockByClass.get(cfg.parent_class_code ?? code) ?? null;
    const r = row(blockKey ? blockIdByKey.get(blockKey) ?? null : null);
    r.bookDep += cfg.dep_for_year_rounded ?? 0;
    r.bookClosingWdv += cfg.closing_wdv_rounded ?? 0;
  }
  for (const d of draft.disposals) {
    row(d.itBlockEnumId != null ? String(d.itBlockEnumId) : null).bookProfitLoss += d.profitLossOnDisposal ?? 0;
  }

  const rows = Array.from(rowMap.values()).sort((a, b) =>
    (a.itBlockEnumId == null ? 1 : 0) - (b.itBlockEnumId == null ? 1 : 0) || a.label.localeCompare(b.label));
  const totals = rows.reduce((t, r) => ({
    bookDep: t.bookDep + r.bookDep,
    bookClosingWdv: t.bookClosingWdv + r.bookClosingWdv,
    bookProfitLoss: t.bookProfitLoss + r.bookProfitLoss,
    itDep: t.itDep + r.itDep,
    itClosingWdv: t.itClosingWdv + r.itClosingWdv,
    s50GainLoss: t.s50GainLoss + r.s50GainLoss,
  }), { bookDep: 0, bookClosingWdv: 0, bookProfitLoss: 0, itDep: 0, itClosingWdv: 0, s50GainLoss: 0 });
  const p = draft.roundingPrecision;
  return {
    rows,
    totals,
    netAdjustmentToProfit: roundTo(totals.bookDep - totals.itDep - totals.bookProfitLoss, p),
  };
};

// ---------------------------------------------------------------------------
// Save mapping
// ---------------------------------------------------------------------------

export const draftToSaveRequest = (draft: DepDraft, rowVersion: number): SaveDepCalcRequest => ({
  roundingPrecision: draft.roundingPrecision,
  taxRate: draft.taxRate,
  bookProrataConvention: draft.bookProrataConvention,
  bookRatesFollowIt: draft.bookRatesFollowIt,
  dtEnabled: draft.dtEnabled,
  depToDisposalDate: draft.depToDisposalDate,
  dtOpeningPosition: draft.dtOpeningPosition,
  dtClosingPositionActual: draft.dtClosingPositionActual,
  dtClosingPositionRounded: draft.dtClosingPositionRounded,
  dtChargeForYearActual: draft.dtChargeForYearActual,
  dtChargeForYearRounded: draft.dtChargeForYearRounded,
  classConfig: draft.classConfig,
  additions: draft.additions.map((a, i) => ({
    scheduleId: a.scheduleId,
    scheduleClassId: a.scheduleClassId,
    customClassLocalId: a.customClassLocalId,
    subClassLocalId: a.subClassLocalId,
    ledgerName: a.ledgerName,
    description: a.description,
    additionDate: a.additionDate,
    putToUseDate: a.putToUseDate,
    amount: a.amount,
    itBlockEnumId: a.itBlockEnumId,
    itRateOverride: a.itRateOverride,
    displayOrder: i,
    daysUsed: a.daysUsed,
    isLt180Days: a.isLt180Days,
    bookDepActual: a.bookDepActual,
    bookDepRounded: a.bookDepRounded,
    itDepIndicativeActual: a.itDepIndicativeActual,
    itDepIndicativeRounded: a.itDepIndicativeRounded,
  })),
  disposals: draft.disposals.map((d, i) => ({
    scheduleId: d.scheduleId,
    scheduleClassId: d.scheduleClassId,
    customClassLocalId: d.customClassLocalId,
    subClassLocalId: d.subClassLocalId,
    ledgerName: d.ledgerName,
    description: d.description,
    originalCost: d.originalCost,
    accumulatedDepreciation: d.accumulatedDepreciation,
    disposalDate: d.disposalDate,
    disposalProceeds: d.disposalProceeds,
    itBlockEnumId: d.itBlockEnumId,
    displayOrder: i,
    depToDisposalActual: d.depToDisposalActual,
    depToDisposalRounded: d.depToDisposalRounded,
    netBookValue: d.netBookValue,
    profitLossOnDisposal: d.profitLossOnDisposal,
  })),
  itBlocks: draft.itBlocks.map(b => ({
    itBlockEnumId: b.itBlockEnumId,
    openingWdv: b.openingWdv,
    rate: b.rate,
    additionsGe180: b.additionsGe180,
    additionsLt180: b.additionsLt180,
    disposalProceeds: b.disposalProceeds,
    itDepActual: b.itDepActual,
    itDepRounded: b.itDepRounded,
    closingWdvActual: b.closingWdvActual,
    closingWdvRounded: b.closingWdvRounded,
    isBlockNegative: b.isBlockNegative,
    isMarkedExhausted: b.isMarkedExhausted,
    itProfitLoss: b.itProfitLoss,
  })),
  rowVersion,
});
